/**
 * @voltxampere/ui — Rolling sample buffer (Sprint 15)
 *
 * Per-channel ring buffer capped at `SAMPLE_BUFFER_CAP` (100 000)
 * sample points. Live mode pushes one sample per transient frame
 * tick; the buffer drops the oldest entry once the cap is hit.
 * Two parallel `Float64Array`s keep `time` and `value` so the
 * waveform renderer can slice into the buffer without allocating
 * an object per sample (Sprint 15 Karar 2 perf budget — 60 fps at
 * 100k × 6 channels = 600k samples per frame envelope).
 *
 * The slice API returns *flat* views into copy-out arrays, never
 * the underlying ring storage. This keeps the renderer ignorant
 * of the head pointer and lets callers iterate with a plain
 * monotonic index.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
import { SAMPLE_BUFFER_CAP } from "./types.js";
export class SampleBuffer {
    cap;
    values;
    times;
    /** Where the next write lands. Wraps once `size === cap`. */
    head = 0;
    /** Number of valid samples currently held. Caps at `cap`. */
    currentSize = 0;
    constructor(cap = SAMPLE_BUFFER_CAP) {
        if (cap <= 0 || !Number.isInteger(cap)) {
            throw new Error(`SampleBuffer cap must be a positive integer, got ${String(cap)}`);
        }
        this.cap = cap;
        this.values = new Float64Array(cap);
        this.times = new Float64Array(cap);
    }
    /** Number of samples currently buffered. Caps at the constructor cap. */
    get size() {
        return this.currentSize;
    }
    /** Appendable cap — exposed for assertions / UI hints. */
    get capacity() {
        return this.cap;
    }
    /** True once the ring has wrapped and is dropping the oldest sample. */
    get full() {
        return this.currentSize >= this.cap;
    }
    /**
     * Append one (time, value) sample. NaN / non-finite inputs are
     * stored as-is so downstream consumers can detect signal gaps;
     * the measurement helpers null-guard explicitly.
     */
    push(time, value) {
        this.times[this.head] = time;
        this.values[this.head] = value;
        this.head = (this.head + 1) % this.cap;
        if (this.currentSize < this.cap)
            this.currentSize += 1;
    }
    /** Drop every sample. Used on circuit swap or buffer reset. */
    clear() {
        this.head = 0;
        this.currentSize = 0;
    }
    /**
     * Return every buffered sample in chronological (oldest → newest)
     * order. Allocates two new typed arrays sized to `size` — the
     * renderer slices off this once per frame, so the alloc cost
     * (one pair per channel per frame) is acceptable on Sprint 15's
     * 100k × 6 envelope.
     */
    snapshot() {
        const n = this.currentSize;
        const out = { times: new Float64Array(n), values: new Float64Array(n) };
        if (n === 0)
            return out;
        if (!this.full) {
            // Buffer hasn't wrapped — head is the next write position,
            // samples 0..head-1 are chronological.
            out.times.set(this.times.subarray(0, n));
            out.values.set(this.values.subarray(0, n));
            return out;
        }
        // Wrapped: the chronological order is [head..cap) then [0..head).
        const tail = this.cap - this.head;
        out.times.set(this.times.subarray(this.head, this.cap), 0);
        out.values.set(this.values.subarray(this.head, this.cap), 0);
        out.times.set(this.times.subarray(0, this.head), tail);
        out.values.set(this.values.subarray(0, this.head), tail);
        return out;
    }
    /**
     * Return the slice of samples whose `t` falls inside
     * `[startTime, endTime]` inclusive. Linear scan over the
     * snapshot — Sprint 15's 100k cap fits in a single pass at
     * <0.5 ms on modern desktop CPUs.
     */
    slice(startTime, endTime) {
        const snap = this.snapshot();
        if (snap.times.length === 0)
            return snap;
        let lo = 0;
        let hi = snap.times.length;
        // Bisect lo to first index with t >= startTime.
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            const tmid = snap.times[mid] ?? Number.POSITIVE_INFINITY;
            if (tmid < startTime)
                lo = mid + 1;
            else
                hi = mid;
        }
        // Bisect hi for first index with t > endTime.
        let hi2 = snap.times.length;
        let lo2 = lo;
        while (lo2 < hi2) {
            const mid = (lo2 + hi2) >>> 1;
            const tmid = snap.times[mid] ?? Number.NEGATIVE_INFINITY;
            if (tmid <= endTime)
                lo2 = mid + 1;
            else
                hi2 = mid;
        }
        const length = hi2 - lo;
        return {
            times: snap.times.subarray(lo, lo + length),
            values: snap.values.subarray(lo, lo + length),
        };
    }
}
//# sourceMappingURL=sample-buffer.js.map